import { Select, Tooltip } from '@chakra-ui/react';
import { useEffect, useState, memo } from 'react';
import { BasicCategory } from 'src/api/v1-api';
import { buildCategoryTree } from 'src/utils/common';
import { ReactElement } from 'react';
import useRequest from 'src/utils/hooks/useRequest';
import { isEmpty } from 'lodash';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
interface CategorySelectorProps {
  marketplaceId: number | undefined;
  selectedCategoryId?: string | undefined;
  onCategorySelect: (category: BasicCategory | null) => void;
  disabled?: boolean;
  disabledReason?: string;
  isRequired?: boolean;
  mode?:
    | 'listing-create'
    | 'listing-create-admin'
    | 'category-admin'
    | 'provide-categories'
    | 'save-search';
  isPayToList?: boolean;
  handlePayToList?: React.Dispatch<React.SetStateAction<boolean>>;
  categories?: BasicCategory[];
  currentlyEditingCategoryId?: number;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  marketplaceId,
  selectedCategoryId,
  onCategorySelect,
  disabled = false,
  disabledReason = '',
  isRequired,
  mode,
  isPayToList = false,
  handlePayToList,
  categories: providedCategories,
  currentlyEditingCategoryId,
}) => {
  // Hooks
  const showToast = useSingleToast();
  const { get } = useRequest();

  const [rawCategories, setRawCategories] = useState<BasicCategory[]>(providedCategories ?? []);
  const [categories, setCategories] = useState<BasicCategory[]>(providedCategories ?? []);
  const [categoryTree, setCategoryTree] = useState<BasicCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(
    isEmpty(providedCategories) || mode !== 'provide-categories',
  );
  const [selectedCategory, setSelectedCategory] = useState<BasicCategory | null>(null);

  // Filter out categories that all children are disabled or not visible
  const filterOutParentCategories = (cats: BasicCategory[]): BasicCategory[] => {
    return cats
      .map((cat) => {
        if (cat.children && cat.children.length > 0) {
          const filteredChildren = filterOutParentCategories(cat.children);
          return { ...cat, children: filteredChildren };
        }
        return cat;
      })
      .filter(
        (cat) =>
          !cat.children ||
          cat.children.length === 0 ||
          cat.children.some((child) => !child.isDisabled && child.isVisible !== false),
      );
  };

  const flagIneligibleCategories = (categories: BasicCategory[]): BasicCategory[] => {
    if (mode === 'listing-create') {
      // Filter out categories with negative positions and those with a CTA link in listing-create mode
      const visibleCategories = categories
        .filter(
          (category) =>
            category.position >= 0 &&
            !category.cta_link &&
            !(category.functional?.hidden_from_browse === true),
        )
        .map((category) => ({
          ...category,
          isDisabled: !category.functional.can_create_listings,
          isVisible:
            category.functional.create_listing_category_select_format !== 'pay_to_list_parent' &&
            category.functional.create_listing_category_select_format !== 'pay_to_list_child',
        }));

      return filterOutParentCategories(visibleCategories);
    }
    if (mode === 'listing-create-admin') {
      // Filter out categories with negative positions and those with a CTA link in listing-create mode
      const visibleCategories = categories.filter(
        (category) =>
          category.position >= 0 &&
          !category.cta_link &&
          !(category.functional?.hidden_from_browse === true),
      );
      return visibleCategories.map((category) => ({
        ...category,
        isDisabled:
          !category.functional.can_create_listings ||
          category.functional.create_listing_category_select_format === 'pay_to_list_parent' ||
          category.functional.create_listing_category_select_format === 'pay_to_list_child',
        isVisible: true,
      }));
    }
    if (mode === 'save-search') {
      // Keep all categories regardless of position in save-search mode
      return categories.map((category) => ({
        ...category,
        isDisabled: Boolean(category.cta_link),
        isVisible: true,
      }));
    }
    if (mode === 'category-admin') {
      // For category-admin mode, keep all categories but disable the currently editing one
      const visibleCategories = categories;
      return visibleCategories.map((category) => ({
        ...category,
        isDisabled: category.id === currentlyEditingCategoryId,
        isVisible: true,
      }));
    }
    // For other modes, filter out negative position categories
    const visibleCategories = categories.filter((category) => category.position >= 0);
    return visibleCategories;
  };

  const handleCategorySelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value);
    const category = categories?.find((c) => c.id === value);
    setSelectedCategory(category || null);
    onCategorySelect(category || null);
  };

  const fetchCategories = async () => {
    if (!marketplaceId) return;

    try {
      setCategoriesLoading(true);
      const fetchUrl = `/marketplaces/${marketplaceId}/${
        mode === 'category-admin' ? 'all_categories' : 'categories'
      }/`;
      const shouldUseToken = mode === 'category-admin' ? true : false;
      // TODO: /categories/ returns CategoryInList, not BasicCategory
      const response = await get<BasicCategory[]>({
        url: fetchUrl,
        useToken: shouldUseToken,
      });
      setRawCategories(response || []);
      const flaggedCategories = flagIneligibleCategories(response);

      setCategories(flaggedCategories || []);

      // Auto-select if there's only one visible category after filtering and processing
      // Don't auto-select in category-admin mode
      if (flaggedCategories && flaggedCategories.length === 1 && mode !== 'category-admin') {
        setSelectedCategory(flaggedCategories[0]);
        onCategorySelect(flaggedCategories[0]);
      }
    } catch (error) {
      showToast({
        title:
          'There was a problem fetching categories. Please try refreshing the page or contact support.',
        status: 'error',
        duration: 9000,
        isClosable: true,
        sentryAlert: {
          error: new Error('Error fetching categories'),
          level: 'error',
          extras: {
            marketplaceId,
            mode,
          },
        },
      });
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    if (!isEmpty(providedCategories) || mode === 'provide-categories') {
      return;
    }

    fetchCategories();
  }, [marketplaceId, providedCategories, mode]);

  useEffect(() => {
    if (selectedCategoryId && categories.length > 0) {
      const selectedCategory = categories.find(
        (category) => category.id == parseInt(selectedCategoryId || ''),
      );
      setSelectedCategory(selectedCategory || null);
      onCategorySelect(selectedCategory || null);
      if (handlePayToList && isPayToList) {
        handlePayToList(true);
      }
    } else {
      setSelectedCategory(null);
    }
  }, [selectedCategoryId, categories]);

  useEffect(() => {
    setCategoryTree(buildCategoryTree(categories || []));
  }, [categories]);

  useEffect(() => {
    if (mode === 'provide-categories' && !isEmpty(providedCategories)) {
      setRawCategories(providedCategories ?? []);
      setCategories(providedCategories ?? []);
      setCategoriesLoading(false);
    }
  }, [providedCategories]);

  // Re-process categories when currentlyEditingCategoryId or mode changes
  useEffect(() => {
    if (rawCategories.length > 0 && mode === 'category-admin') {
      const reflaggedCategories = flagIneligibleCategories(rawCategories);
      setCategories(reflaggedCategories);
    }
  }, [currentlyEditingCategoryId, rawCategories, mode]);

  const renderCategoryOptions: (
    categoryTree: BasicCategory[],
    prefix?: string,
  ) => ReactElement[] = (categoryTree, prefix = '') => {
    // Sort categories by position first, then by id for consistent ordering
    const sortedCategories = [...categoryTree].sort((a, b) => {
      if (a.position === b.position) {
        return a.id - b.id;
      }
      return a.position - b.position;
    });

    return sortedCategories.flatMap((cat) => {
      if (cat.isVisible === undefined || cat.isVisible) {
        return [
          <option key={cat.id} value={cat.id} disabled={cat.isDisabled}>
            {prefix + cat.name}
          </option>,
          ...renderCategoryOptions(cat.children || [], prefix + '\u00A0\u00A0'),
        ];
      }
      return renderCategoryOptions(cat.children || [], prefix + '\u00A0\u00A0');
    });
  };

  useEffect(() => {
    if (selectedCategoryId && categories.length > 0) {
      const selectedCategory = categories.find(
        (category) => category.id == parseInt(selectedCategoryId || ''),
      );
      setSelectedCategory(selectedCategory || null);
      onCategorySelect(selectedCategory || null);
    }
  }, [selectedCategoryId, categories]);
  return (
    <Tooltip label={disabled ? disabledReason : ''} isDisabled={!disabled}>
      <Select
        id="category-select"
        value={selectedCategory ? selectedCategory.id.toString() : ''}
        onChange={handleCategorySelectChange}
        placeholder="Select Category"
        disabled={disabled}
        isRequired={isRequired}
        isDisabled={categoriesLoading || disabled}
        data-test="category-selector"
      >
        {categoriesLoading ? (
          <option value="" disabled>
            Loading...
          </option>
        ) : (
          renderCategoryOptions(categoryTree)
        )}
      </Select>
    </Tooltip>
  );
};

export default memo(CategorySelector);
